import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface PublicConfig {
  categories: { value: string; label: string }[];
  relationships: { value: string; label: string }[];
  legalText: string;
  branding: string;
}

export interface CreateDenounceRequest {
  category: string;
  relationship: string;
  isAnonymous: boolean;
  reporterName?: string;
  reporterEmail?: string;
  reporterPhone?: string;
  reporterCountryCode?: string;
  reporterDocumentType?: string;
  reporterDocumentNumber?: string;
  accusedName: string;
  accusedRole?: string;
  location: string;
  incidentDate: string;
  description: string;
  attachmentKeys?: string[];
  recaptchaScore?: number;
}

export interface CreateDenounceResponse {
  trackingId: string;
  pin: string;
}

export interface TrackingResponse {
  trackingId: string;
  status: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  attachments: { id: string; fileName: string; fileType: string }[];
  messages: {
    id: string;
    authorType: 'ADMIN' | 'REPORTER';
    content: string;
    createdAt: string;
    attachments: { id: string; fileName: string; fileType: string }[];
  }[];
}

@Injectable({ providedIn: 'root' })
export class DenouncePublicApiService {
  private apiUrl = `${environment.apiNest}denounces/public`;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  private handleError(error: HttpErrorResponse) {
    let message = 'Ha ocurrido un error. Intente nuevamente.';
    if (error.error?.message) {
      message = error.error.message;
    }
    return throwError(() => ({ statusCode: error.status, message }));
  }

  getConfig(): Observable<PublicConfig> {
    return this.http
      .get<PublicConfig>(`${this.apiUrl}/config`)
      .pipe(catchError(this.handleError));
  }

  createDenounce(data: CreateDenounceRequest): Observable<CreateDenounceResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<CreateDenounceResponse>(`${this.apiUrl}/create`, data)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  track(trackingId: string, pin: string): Observable<TrackingResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<TrackingResponse>(`${this.apiUrl}/track`, { trackingId, pin })
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  sendMessage(
    trackingId: string,
    pin: string,
    content: string,
    attachmentKeys?: string[],
  ): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/track/message`, {
        trackingId,
        pin,
        content,
        attachmentKeys,
      })
      .pipe(catchError(this.handleError));
  }

  uploadFile(fileBase64: string, fileName: string): Observable<{
    storageKey: string;
    fileName: string;
    fileSize: number;
    fileType: string;
  }> {
    return this.http
      .post<any>(`${this.apiUrl}/upload`, { fileBase64, fileName })
      .pipe(catchError(this.handleError));
  }

  getAttachmentUrl(
    trackingId: string,
    pin: string,
    attachmentId: string,
  ): Observable<{ url: string; fileName: string; fileType: string }> {
    return this.http
      .post<any>(`${this.apiUrl}/attachment-url`, {
        trackingId,
        pin,
        attachmentId,
      })
      .pipe(catchError(this.handleError));
  }

  recoverPin(
    trackingId: string,
    email: string,
  ): Observable<{ ok: boolean; message: string }> {
    this.loadingSubject.next(true);
    return this.http
      .post<any>(`${this.apiUrl}/recover-pin`, { trackingId, email })
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  exportTrackingPdf(trackingId: string, pin: string): Observable<Blob> {
    return this.http.post(
      `${this.apiUrl}/export-tracking`,
      { trackingId, pin },
      { responseType: 'blob' },
    );
  }
}
